import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, Search } from "lucide-react";
import articles, {
  ARTICLE_CATEGORIES,
} from "../articles";
import {
  createArticleSearchParams,
  getArticleArchive,
  paginateArticles,
  parseArticleSearchParams,
} from "../utils/articleDiscovery";
import "../styles/articles.css";

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

const formatArticleDate = (date) => dateFormatter.format(new Date(`${date}T00:00:00`));

const getPageItems = (page, pageCount) => {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }

  const pages = new Set([1, pageCount, page - 1, page, page + 1]);
  const visiblePages = [...pages]
    .filter((item) => item > 0 && item <= pageCount)
    .sort((a, b) => a - b);
  const items = [];

  visiblePages.forEach((item, index) => {
    if (index > 0 && item - visiblePages[index - 1] > 1) items.push("ellipsis");
    items.push(item);
  });

  return items;
};

const ArticleCard = ({ article }) => {
  const hiddenTagCount = Math.max(0, (article.topics || []).length - 2);

  return (
    <Link to={`/articles/${article.slug}`} className="article-card-link">
      <article className="library-card">
        <div className="library-card__media">
          <img
            src={article.cover_image}
            srcSet={article.cover_image_srcset}
            sizes="(max-width: 720px) 100vw, (max-width: 1100px) 50vw, 33vw"
            alt=""
            loading="lazy"
            decoding="async"
            width="640"
            height="360"
            className="library-card__image"
          />
        </div>
        <div className="library-card__body">
          <div className="library-card__meta">
            <span className="category-label">{article.category}</span>
            <time dateTime={article.date}>{formatArticleDate(article.date)}</time>
          </div>
          <h3>{article.title}</h3>
          <p className="library-card__summary">{article.summary}</p>
          <div className="library-card__footer">
            <div className="library-card__tags" aria-label="Article tags">
              {(article.topics || []).slice(0, 2).map((topic) => (
                <span key={topic}>{topic}</span>
              ))}
              {hiddenTagCount > 0 ? <span>+{hiddenTagCount}</span> : null}
            </div>
            <span className="library-card__read" aria-hidden="true">
              Read <ArrowRight size={15} />
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
};

const ArticleList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const state = useMemo(() => parseArticleSearchParams(searchParams), [searchParams]);
  const archive = useMemo(() => getArticleArchive(articles, state), [state]);
  const pagination = useMemo(
    () => paginateArticles(archive.articles, state.page),
    [archive.articles, state.page]
  );

  useEffect(() => {
    const canonicalParams = createArticleSearchParams({
      ...state,
      page: pagination.page,
    });

    if (canonicalParams.toString() !== searchParams.toString()) {
      setSearchParams(canonicalParams, { replace: true });
    }
  }, [pagination.page, searchParams, setSearchParams, state]);

  const updateState = (updates, options = {}) => {
    const changesPage = Object.prototype.hasOwnProperty.call(updates, "page");
    const nextState = {
      ...state,
      ...updates,
      page: changesPage ? updates.page : 1,
    };
    setSearchParams(createArticleSearchParams(nextState), options);
  };

  const showFeatured = archive.featured && pagination.page === 1;
  const resultCount = archive.articles.length + (archive.featured ? 1 : 0);
  const hasActiveControls =
    Boolean(state.q) || Boolean(state.category) || state.sort !== "newest" || state.page > 1;
  const pageItems = getPageItems(pagination.page, pagination.pageCount);

  return (
    <main className="article-library">
      <section className="article-library__intro" aria-labelledby="article-library-title">
        <div>
          <p className="article-eyebrow">Ideas & Insights</p>
          <h1 id="article-library-title">
            Ideas at the intersection of data, operations, and leadership
          </h1>
          <p>
            Practical thinking on data, AI, operations, mobility, and leadership—organized
            for discovery as the library grows.
          </p>
        </div>
        <dl className="article-library__stats" aria-label="Library summary">
          <div>
            <dt>Articles</dt>
            <dd>{articles.length}</dd>
          </div>
          <div>
            <dt>Categories</dt>
            <dd>{ARTICLE_CATEGORIES.length}</dd>
          </div>
        </dl>
      </section>

      {showFeatured ? (
        <section className="featured-article" aria-labelledby="featured-article-title">
          <div className="featured-article__media">
            <img
              src={archive.featured.cover_image}
              srcSet={archive.featured.cover_image_srcset}
              sizes="(max-width: 900px) 100vw, 52vw"
              alt=""
              loading="eager"
              decoding="async"
              fetchPriority="high"
              width="1280"
              height="720"
            />
          </div>
          <div className="featured-article__content">
            <p className="featured-article__label">Featured article</p>
            <div className="featured-article__meta">
              <span>{archive.featured.category}</span>
              <time dateTime={archive.featured.date}>
                {formatArticleDate(archive.featured.date)}
              </time>
            </div>
            <h2 id="featured-article-title">{archive.featured.title}</h2>
            <p>{archive.featured.summary}</p>
            <Link to={`/articles/${archive.featured.slug}`} className="featured-article__link">
              Read featured article <ArrowRight size={18} />
            </Link>
          </div>
        </section>
      ) : null}

      <section className="article-discovery" aria-label="Find articles">
        <div className="article-discovery__top-row">
          <label className="article-search">
            <span className="visually-hidden">Search articles</span>
            <Search size={19} aria-hidden="true" />
            <input
              type="search"
              value={state.q}
              onChange={(event) => updateState({ q: event.target.value }, { replace: true })}
              placeholder="Search titles, summaries, categories, or tags"
              autoComplete="off"
            />
          </label>
          <label className="article-sort">
            <span>Sort</span>
            <select
              value={state.sort}
              onChange={(event) => updateState({ sort: event.target.value })}
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="title">Title A–Z</option>
            </select>
          </label>
          {hasActiveControls ? (
            <button
              type="button"
              className="article-clear"
              onClick={() => setSearchParams(new URLSearchParams())}
            >
              Clear all
            </button>
          ) : null}
        </div>

        <div className="article-categories" aria-label="Filter by category">
          <button
            type="button"
            className={!state.category ? "active" : ""}
            aria-pressed={!state.category}
            onClick={() => updateState({ category: "" })}
          >
            All articles
          </button>
          {ARTICLE_CATEGORIES.map((category) => (
            <button
              key={category}
              type="button"
              className={state.category === category ? "active" : ""}
              aria-pressed={state.category === category}
              onClick={() => updateState({ category })}
            >
              {category}
            </button>
          ))}
        </div>
      </section>

      <section className="article-archive" aria-labelledby="article-archive-title">
        <div className="article-archive__heading">
          <div>
            <p className="article-eyebrow">Browse the library</p>
            <h2 id="article-archive-title">
              {archive.editorial ? "Latest articles" : "Article results"}
            </h2>
          </div>
          <p aria-live="polite">
            {resultCount} {resultCount === 1 ? "article" : "articles"}
          </p>
        </div>

        {pagination.items.length > 0 ? (
          <div className="article-grid">
            {pagination.items.map((article) => (
              <ArticleCard key={article.slug} article={article} />
            ))}
          </div>
        ) : (
          <div className="article-empty" role="status">
            <h3>No articles found</h3>
            <p>Try another phrase or remove the active category filter.</p>
            <button type="button" onClick={() => setSearchParams(new URLSearchParams())}>
              Show all articles
            </button>
          </div>
        )}

        {pagination.pageCount > 1 ? (
          <nav className="article-pagination" aria-label="Article pages">
            <button
              type="button"
              onClick={() => updateState({ page: pagination.page - 1 })}
              disabled={pagination.page === 1}
              aria-label="Previous article page"
            >
              <ChevronLeft size={18} /> <span>Previous</span>
            </button>
            <div className="article-pagination__pages">
              {pageItems.map((item, index) =>
                item === "ellipsis" ? (
                  <span key={`ellipsis-${index}`} aria-hidden="true">
                    …
                  </span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    className={item === pagination.page ? "active" : ""}
                    aria-current={item === pagination.page ? "page" : undefined}
                    aria-label={`Page ${item}`}
                    onClick={() => updateState({ page: item })}
                  >
                    {item}
                  </button>
                )
              )}
            </div>
            <button
              type="button"
              onClick={() => updateState({ page: pagination.page + 1 })}
              disabled={pagination.page === pagination.pageCount}
              aria-label="Next article page"
            >
              <span>Next</span> <ChevronRight size={18} />
            </button>
          </nav>
        ) : null}
      </section>
    </main>
  );
};

export default ArticleList;
